import { Result, ok, err } from "neverthrow";
import {
  CLIENT_STRINGS as _$,
  CLIENT_STRINGS_REF as _$REF,
  ITEM_BY_REF,
  STAT_BY_MATCH_STR,
  BaseType,
  ITEM_BY_TRANSLATED,
  TRADE_ITEM_BY_REF,
  AUGMENT_DATA_BY_TRADE_ID,
  CATALYST_TYPES,
  CATALYST_TO_TAG,
} from "@/assets/data";
import { ModifierType, StatCalculated, sumStatsByModType } from "./modifiers";
import {
  linesToStatStrings,
  tryParseTranslation,
  getRollOrMinmaxAvg,
} from "./stat-translations";
import { GEM, ItemCategory } from "./meta";
import {
  IncursionRoom,
  ParsedItem,
  ItemInfluence,
  ItemRarity,
  itemIsModifiable,
  EditorItem,
} from "./ParsedItem";
import { magicBasetype } from "./magic-name";
import {
  parseModType,
  ModifierInfo,
  ParsedModifier,
  ENCHANT_LINE,
  SCOURGE_LINE,
  IMPLICIT_LINE,
  AUGMENT_LINE,
  isModInfoLine,
  groupLinesByMod,
  parseModInfoLine,
  ADDED_AUGMENT_LINE,
} from "./advanced-mod-desc";
import { calcPropPercentile, QUALITY_STATS } from "./calc-q20";
import { AppConfig } from "@/web/Config";
import { buildEditorItems, getSavedAugments } from "./augment-builder";
import { useAugment } from "@/web/price-check/item-editor/augment";
import { avg, combinations } from "./utils";

type SectionParseResult =
  | "SECTION_PARSED"
  | "SECTION_SKIPPED"
  | "PARSER_SKIPPED";

type ParserFn = (section: string[], item: ParserState) => SectionParseResult;
type VirtualParserFn = (item: ParserState) => Result<never, string> | void;

const LANGUAGE_DETECTOR = [
  {
    lang: "en",
    displayLang: "English",
    itemClassLine: "Item Class: ",
    rarityLine: "Rarity: ",
  },
  {
    lang: "ru",
    displayLang: "Русский",
    itemClassLine: "Класс предмета: ",
    rarityLine: "Редкость: ",
  },
  {
    lang: "fr",
    displayLang: "Français",
    itemClassLine: "Classe d'objet: ",
    rarityLine: "Rareté: ",
  },
  {
    lang: "de",
    displayLang: "Deutsch",
    itemClassLine: "Gegenstandsklasse: ",
    rarityLine: "Seltenheit: ",
  },
  {
    lang: "pt",
    displayLang: "Português (Brasil)",
    itemClassLine: "Classe do Item: ",
    rarityLine: "Raridade: ",
  },
  {
    lang: "es",
    displayLang: "Español",
    itemClassLine: "Clase de objeto: ",
    rarityLine: "Rareza: ",
  },
  {
    lang: "th",
    displayLang: "ไทย",
    itemClassLine: "ชนิดไอเทม: ",
    rarityLine: "Rarity: ",
  },
  {
    lang: "ko",
    displayLang: "한국어",
    itemClassLine: "아이템 종류: ",
    rarityLine: "아이템 희귀도: ",
  },
  {
    lang: "cmn-Hant",
    displayLang: "正體中文",
    itemClassLine: "物品種類: ",
    rarityLine: "稀有度: ",
  },
  {
    lang: "cmn-Hans",
    displayLang: "普通话",
    itemClassLine: "物品类别: ",
    rarityLine: "Rarity: ",
  },
  {
    lang: "ja",
    displayLang: "日本語",
    itemClassLine: "アイテムクラス: ",
    rarityLine: "レアリティ: ",
  },
];

export interface ParserState extends ParsedItem {
  name: string;
  baseType: string | undefined;
  infoVariants: BaseType[];
}

const parsers: Array<ParserFn | { virtual: VirtualParserFn }> = [
  parseUnidentified,
  { virtual: parseSuperior },
  { virtual: parseExceptional },
  { virtual: parseRuneforged },
  parseSynthesised,
  parseCategoryByHelpText,
  { virtual: normalizeName },
  { virtual: findInDatabase },
  // -----------
  parseItemLevel,
  parseRequirements,
  parseTalismanTier,
  parseGem,
  parseArmour,
  parseWeapon,
  parseCaster,
  parseFlask,
  parseJewelery,
  parseCharmSlots,
  parseSpirit,
  parsePriceNote,
  parseTimelostRadius,
  parseStackSize,
  parseCorrupted,
  parseFoil,
  parseInfluence,
  parseMap,
  parseWaystone,
  parseSockets,
  parseAugmentSockets,
  parseHeistBlueprint,
  parseTrials,
  parseAreaLevel,
  parseAtzoatlRooms,
  parseMirroredTablet,
  parseFilledCoffin,
  parseMirrored,
  parseSanctified,
  parseSentinelCharge,
  parseLogbookArea,
  parseLogbookArea,
  parseLogbookArea,
  parseModifiers, // enchant
  parseModifiers, // augment
  parseModifiers, // implicit
  parseModifiers, // grant skill
  parseModifiers, // explicit
  { virtual: transformToLegacyModifiers },
  { virtual: parseFractured },
  { virtual: parseBlightedMap },
  { virtual: applyAugmentSockets },
  { virtual: applyElementalAdded },
  { virtual: pickCorrectVariant },
  { virtual: calcBasePercentile },
];

export function parseClipboard(clipboard: string): Result<ParsedItem, string> {
  try {
    performance.mark("parse-start");
    let sections = itemTextToSections(clipboard);

    if (sections[0][2] === _$.CANNOT_USE_ITEM) {
      sections[0].pop(); // remove CANNOT_USE_ITEM line
      sections[1].unshift(...sections[0]); // prepend item class & rarity into second section
      sections.shift(); // remove first section where CANNOT_USE_ITEM line was
    }
    const parsed = parseNamePlate(sections[0]);
    if (!parsed.isOk()) return parsed;

    sections.shift();
    parsed.value.rawText = clipboard;

    performance.mark("parse-start-parsers");
    // each section can be parsed at most by one parser
    // and each parser can only be used to parse one section
    for (const parser of parsers) {
      if (typeof parser === "object") {
        const error = parser.virtual(parsed.value);
        if (error) return error;
        continue;
      }
      for (const section of sections) {
        const result = parser(section, parsed.value);
        if (result === "SECTION_PARSED") {
          sections = sections.filter((s) => s !== section);
          break;
        } else if (result === "PARSER_SKIPPED") {
          break;
        }
      }
    }
    performance.mark("parse-end");
    return Object.freeze(parsed);
  } catch (e) {
    console.log(e);
    return err("item.parse_error");
  }
}

function itemTextToSections(text: string) {
  performance.mark("itemTextToSections");
  const lines = text.split(/\r?\n/);
  if (lines[lines.length - 1] === "") {
    lines.pop();
  }

  const sections: string[][] = [[]];
  lines.reduce((section, line) => {
    if (line !== "--------") {
      section.push(line);
      return section;
    } else {
      const section: string[] = [];
      sections.push(section);
      return section;
    }
  }, sections[0]);
  return sections.filter((section) => section.length);
}

function normalizeName(item: ParserState) {
  performance.mark("normalizeName");
  if (item.rarity === ItemRarity.Magic) {
    const baseType = magicBasetype(item.name);
    if (baseType) {
      item.name = baseType;
    }
  }

  if (item.rarity === ItemRarity.Normal || item.rarity === ItemRarity.Rare) {
    if (item.baseType) {
      if (_$REF.MAP_BLIGHTED.test(item.baseType)) {
        item.baseType = _$REF.MAP_BLIGHTED.exec(item.baseType)![1];
      } else if (_$REF.MAP_BLIGHT_RAVAGED.test(item.baseType)) {
        item.baseType = _$REF.MAP_BLIGHT_RAVAGED.exec(item.baseType)![1];
      } else if (_$.MAP_BLIGHTED.test(item.baseType)) {
        item.baseType = _$.MAP_BLIGHTED.exec(item.baseType)![1];
      } else if (_$.MAP_BLIGHT_RAVAGED.test(item.baseType)) {
        item.baseType = _$.MAP_BLIGHT_RAVAGED.exec(item.baseType)![1];
      }
    } else {
      if (_$REF.MAP_BLIGHTED.test(item.name)) {
        item.name = _$REF.MAP_BLIGHTED.exec(item.name)![1];
      } else if (_$REF.MAP_BLIGHT_RAVAGED.test(item.name)) {
        item.name = _$REF.MAP_BLIGHT_RAVAGED.exec(item.name)![1];
      } else if (_$.MAP_BLIGHTED.test(item.name)) {
        item.name = _$.MAP_BLIGHTED.exec(item.name)![1];
      } else if (_$.MAP_BLIGHT_RAVAGED.test(item.name)) {
        item.name = _$.MAP_BLIGHT_RAVAGED.exec(item.name)![1];
      }
    }
  }

  if (item.category === ItemCategory.MetamorphSample) {
    if (_$.METAMORPH_BRAIN.test(item.name)) {
      item.name = "Metamorph Brain";
    } else if (_$.METAMORPH_EYE.test(item.name)) {
      item.name = "Metamorph Eye";
    } else if (_$.METAMORPH_LUNG.test(item.name)) {
      item.name = "Metamorph Lung";
    } else if (_$.METAMORPH_HEART.test(item.name)) {
      item.name = "Metamorph Heart";
    } else if (_$.METAMORPH_LIVER.test(item.name)) {
      item.name = "Metamorph Liver";
    }
  }
}

function findInDatabase(item: ParserState) {
  performance.mark("findInDatabase");
  let info: BaseType[] | undefined;
  if (item.category === ItemCategory.DivinationCard) {
    info = ITEM_BY_REF("DIVINATION_CARD", item.name);
  } else if (item.category === ItemCategory.CapturedBeast) {
    info = ITEM_BY_REF("CAPTURED_BEAST", item.baseType ?? item.name);
  } else if (item.category && GEM.has(item.category)) {
    info = ITEM_BY_REF("GEM", item.name);
  } else if (item.category === ItemCategory.MetamorphSample) {
    info = ITEM_BY_REF("ITEM", item.name);
  } else if (item.category === ItemCategory.Voidstone) {
    info = ITEM_BY_REF("ITEM", "Charged Compass");
  } else if (item.rarity === ItemRarity.Unique && !item.isUnidentified) {
    info = ITEM_BY_REF("UNIQUE", item.name);
  } else {
    info = ITEM_BY_REF("ITEM", item.baseType ?? item.name);
  }

  if (!info?.length) {
    // BUG[UPSTREAM]: https://www.pathofexile.com/forum/view-thread/3913283
    if (item.category === ItemCategory.DivinationCard) {
      info = ITEM_BY_TRANSLATED("DIVINATION_CARD", item.name);
    } else if (item.category === ItemCategory.CapturedBeast) {
      info = ITEM_BY_TRANSLATED("CAPTURED_BEAST", item.baseType ?? item.name);
    } else if (item.category && GEM.has(item.category)) {
      info = ITEM_BY_TRANSLATED("GEM", item.name);
    } else if (item.category === ItemCategory.MetamorphSample) {
      info = ITEM_BY_TRANSLATED("ITEM", item.name);
    } else if (item.category === ItemCategory.Voidstone) {
      info = ITEM_BY_TRANSLATED("ITEM", "Charged Compass");
    } else if (item.rarity === ItemRarity.Unique && !item.isUnidentified) {
      info = ITEM_BY_TRANSLATED("UNIQUE", item.name);
    } else {
      info = ITEM_BY_TRANSLATED("ITEM", item.baseType ?? item.name);
    }
  }
  if (!info?.length) {
    // First attempt to find item in trade items data
    info = TRADE_ITEM_BY_REF({
      name: item.name,
      category: item.category,
      rarity: item.rarity,
      baseType: item.baseType,
    });
    if (!info?.length) {
      return err("item.unknown");
    }
  }

  if (info[0].unique) {
    const uniqueInfo = info.filter(
      (info) => info.unique!.base === item.baseType,
    );
    if (uniqueInfo?.length) {
      info = uniqueInfo;
    } else if (item.baseType) {
      const baseInfo = ITEM_BY_TRANSLATED("ITEM", item.baseType);
      if (baseInfo?.length) {
        info = info.filter((info) => info.unique!.base === baseInfo[0].refName);
      }
    }
  }
  item.infoVariants = info;
  // choose 1st variant, correct one will be picked at the end of parsing
  item.info = info[0];
  // same for every variant
  if (
    !item.category ||
    item.info.craftable?.category === ItemCategory.Wombgift
  ) {
    if (item.info.craftable) {
      item.category = item.info.craftable.category;
    } else if (item.info.unique) {
      item.category = ITEM_BY_REF(
        "ITEM",
        item.info.unique.base,
      )![0].craftable!.category;
    }
  }
}

function parseMap(section: string[], item: ParsedItem) {
  performance.mark("parseMap");
  if (section[0].startsWith(_$.MAP_TIER)) {
    item.mapTier = Number(section[0].slice(_$.MAP_TIER.length));
    return "SECTION_PARSED";
  }
  return "SECTION_SKIPPED";
}

function parseWaystone(section: string[], item: ParsedItem) {
  performance.mark("parseWaystone");

  if (section[0].startsWith(_$.WAYSTONE_REVIVES) && item.info.map?.tier) {
    // we are a map now
    item.mapTier = item.info.map.tier;

    for (const line of section) {
      if (line.startsWith(_$.WAYSTONE_REVIVES)) {
        item.mapRevives = parseInt(line.slice(_$.WAYSTONE_REVIVES.length), 10);
        continue;
      }

      if (line.startsWith(_$.WAYSTONE_PACK_SIZE)) {
        item.mapPackSize = parseInt(
          line.slice(_$.WAYSTONE_PACK_SIZE.length),
          10,
        );
        continue;
      }

      if (line.startsWith(_$.WAYSTONE_MAGIC_MONSTERS)) {
        item.mapMagicMonsters = parseInt(
          line.slice(_$.WAYSTONE_MAGIC_MONSTERS.length),
          10,
        );
        continue;
      }

      if (line.startsWith(_$.WAYSTONE_RARE_MONSTERS)) {
        item.mapRareMonsters = parseInt(
          line.slice(_$.WAYSTONE_RARE_MONSTERS.length),
          10,
        );
        continue;
      }

      if (line.startsWith(_$.WAYSTONE_DROP_CHANCE)) {
        item.mapDropChance = parseInt(
          line.slice(_$.WAYSTONE_DROP_CHANCE.length),
          10,
        );
        continue;
      }

      if (line.startsWith(_$.WAYSTONE_RARITY)) {
        item.mapItemRarity = parseInt(
          line.slice(_$.WAYSTONE_RARITY.length),
          10,
        );
        continue;
      }

      if (line.startsWith(_$.WAYSTONE_MONSTER_RARITY)) {
        item.mapMonsterRarity = parseInt(
          line.slice(_$.WAYSTONE_MONSTER_RARITY.length),
          10,
        );
        continue;
      }

      if (line.startsWith(_$.WAYSTONE_EFFECTIVENESS)) {
        item.mapEffectiveness = parseInt(
          line.slice(_$.WAYSTONE_EFFECTIVENESS.length),
          10,
        );
        continue;
      }
    }

    return "SECTION_PARSED";
  }
  return "SECTION_SKIPPED";
}

function parseBlightedMap(item: ParsedItem) {
  performance.mark("parseBlightedMap");
  if (item.category !== ItemCategory.Map) return;

  const calc = item.statsByType.find(
    (calc) =>
      calc.type === ModifierType.Implicit &&
      calc.stat.ref.startsWith("Area is infested with Fungal Growths"),
  );
  if (calc !== undefined) {
    if (calc.sources[0].contributes!.value === 9) {
      item.mapBlighted = "Blight-ravaged";
      item.info.icon = ITEM_BY_REF("ITEM", "Blight-ravaged Map")![0].icon;
    } else {
      item.mapBlighted = "Blighted";
      item.info.icon = ITEM_BY_REF("ITEM", "Blighted Map")![0].icon;
    }
  }
}

function parseFractured(item: ParserState) {
  performance.mark("parseFractured");
  if (item.newMods.some((mod) => mod.info.type === ModifierType.Fractured)) {
    item.isFractured = true;
  }
}

function pickCorrectVariant(item: ParserState) {
  performance.mark("pickCorrectVariant");
  if (!item.info.disc) return;

  for (const variant of item.infoVariants) {
    const cond = variant.disc!;

    if (cond.propAR && !item.armourAR) continue;
    if (cond.propEV && !item.armourEV) continue;
    if (cond.propES && !item.armourES) continue;

    if (cond.mapTier === "W" && !(item.mapTier! <= 5)) continue;
    if (cond.mapTier === "Y" && !(item.mapTier! >= 6 && item.mapTier! <= 10))
      continue;
    if (cond.mapTier === "R" && !(item.mapTier! >= 11)) continue;

    if (
      cond.hasImplicit &&
      !item.statsByType.some(
        (calc) =>
          calc.type === ModifierType.Implicit &&
          calc.stat.ref === cond.hasImplicit!.ref,
      )
    )
      continue;

    if (
      cond.hasExplicit &&
      !item.statsByType.some(
        (calc) =>
          calc.type === ModifierType.Explicit &&
          calc.stat.ref === cond.hasExplicit!.ref,
      )
    )
      continue;

    if (cond.sectionText && !item.rawText.includes(cond.sectionText)) continue;

    item.info = variant;
  }

  // it may happen that we don't find correct variant
  // i.e. corrupted implicit on Two-Stone Ring
}

function parseNamePlate(section: string[]) {
  performance.mark("parseNamePlate");
  let line = section.shift();

  let missingItemClass = false;

  if (!line?.startsWith(_$.ITEM_CLASS)) {
    // HACK: Meta skill gems
    if (line && section.unshift(line) && isItemMissingItemClass(section)) {
      missingItemClass = true;
    } else {
      // figure out if this was an issue with wrong language, or a bug
      console.log(section);
      const langFound = LANGUAGE_DETECTOR.find(({ rarityLine }) =>
        section[1].startsWith(rarityLine),
      );
      if (langFound) {
        return err(
          `item.wrong_language|${langFound.displayLang}|${LANGUAGE_DETECTOR.find(({ lang }) => lang === AppConfig().language)!.displayLang}`,
        );
      }

      return err("item.parse_error");
    }
  }

  line = section.shift();
  let rarityText: string | undefined;
  if (line?.startsWith(_$.RARITY)) {
    rarityText = line.slice(_$.RARITY.length);
    line = section.shift();
  }

  let name: string;
  if (line != null) {
    name = markupConditionParser(line);
  } else {
    return err("item.parse_error");
  }

  line = section.shift();
  const baseType = line && markupConditionParser(line);

  const item: ParserState = {
    rarity: undefined,
    category: undefined,
    name,
    baseType,
    isUnidentified: false,
    isCorrupted: false,
    newMods: [],
    statsByType: [],
    unknownModifiers: [],
    influences: [],
    info: undefined!,
    infoVariants: undefined!,
    rawText: undefined!,
  };

  switch (rarityText) {
    case _$.RARITY_CURRENCY:
      item.category = ItemCategory.Currency;
      break;
    case _$.RARITY_DIVCARD:
      item.category = ItemCategory.DivinationCard;
      break;
    case _$.RARITY_GEM:
      item.category = ItemCategory.Gem;
      break;
    case _$.RARITY_NORMAL:
    case _$.RARITY_QUEST:
      item.rarity = ItemRarity.Normal;
      break;
    case _$.RARITY_MAGIC:
      item.rarity = ItemRarity.Magic;
      break;
    case _$.RARITY_RARE:
      item.rarity = ItemRarity.Rare;
      break;
    case _$.RARITY_UNIQUE:
      item.rarity = ItemRarity.Unique;
      break;
  }

  if (missingItemClass) {
    item.category = ItemCategory.Gem;
  }

  return ok(item);
}

function parseInfluence(section: string[], item: ParsedItem) {
  performance.mark("parseInfluence");
  if (section.length <= 2) {
    const countBefore = item.influences.length;

    for (const line of section) {
      switch (line) {
        case _$.INFLUENCE_CRUSADER:
          item.influences.push(ItemInfluence.Crusader);
          break;
        case _$.INFLUENCE_ELDER:
          item.influences.push(ItemInfluence.Elder);
          break;
        case _$.INFLUENCE_SHAPER:
          item.influences.push(ItemInfluence.Shaper);
          break;
        case _$.INFLUENCE_HUNTER:
          item.influences.push(ItemInfluence.Hunter);
          break;
        case _$.INFLUENCE_REDEEMER:
          item.influences.push(ItemInfluence.Redeemer);
          break;
        case _$.INFLUENCE_WARLORD:
          item.influences.push(ItemInfluence.Warlord);
          break;
      }
    }

    if (countBefore < item.influences.length) {
      return "SECTION_PARSED";
    }
  }
  return "SECTION_SKIPPED";
}

// #region Small Sections
function parseCorrupted(section: string[], item: ParsedItem) {
  performance.mark("parseCorrupted");
  if (
    section[0].trim() === _$.CORRUPTED ||
    section[0].trim() === _$.DOUBLE_CORRUPTED
  ) {
    item.isCorrupted = true;
    return "SECTION_PARSED";
  } else if (section[0] === _$.UNMODIFIABLE) {
    item.isCorrupted = true;
    item.isUnmodifiable = true;
    return "SECTION_PARSED";
  }
  return "SECTION_SKIPPED";
}

function parseFoil(section: string[], item: ParsedItem) {
  performance.mark("parseFoil");
  if (item.rarity !== ItemRarity.Unique) {
    return "PARSER_SKIPPED";
  }
  if (section[0] === _$.FOIL_UNIQUE) {
    item.isFoil = true;
    return "SECTION_PARSED";
  }
  return "SECTION_SKIPPED";
}

function parseUnidentified(section: string[], item: ParsedItem) {
  performance.mark("parseUnidentified");
  const match = section[0].match(_$.UNIDENTIFIED);
  if (match) {
    item.isUnidentified = true;
    if (match.groups!.tier) {
      item.unidentifiedTier = Number(match.groups!.tier);
    }
    return "SECTION_PARSED";
  }
  return "SECTION_SKIPPED";
}

function parseItemLevel(section: string[], item: ParsedItem) {
  performance.mark("parseItemLevel");
  let prefix = _$.ITEM_LEVEL;
  if (item.info.refName === "Filled Coffin") {
    prefix = _$.CORPSE_LEVEL;
  }

  for (const line of section) {
    if (line.startsWith(prefix)) {
      item.itemLevel = Number(line.slice(prefix.length));
      return "SECTION_PARSED";
    }
  }
  return "SECTION_SKIPPED";
}

function parseRequirements(section: string[], item: ParsedItem) {
  performance.mark("parseRequirements");
  if (!section[0].startsWith(_$.REQUIRES)) {
    return "SECTION_SKIPPED";
  }

  if (item.category && GEM.has(item.category)) {
    return "SECTION_SKIPPED";
  }

  const match = section[0].match(_$.REQUIRES_LINE);
  // TODO: remove once validated in other langs
  if (!match) {
    throw new Error("Failed to parse requirements");
  }

  item.requires = {
    level: parseInt(match.groups!.level ?? "0"),
    str: parseInt(match.groups!.str ?? "0"),
    dex: parseInt(match.groups!.dex ?? "0"),
    int: parseInt(match.groups!.int ?? "0"),
  };

  return "SECTION_PARSED";
}

function parseTalismanTier(section: string[], item: ParsedItem) {
  performance.mark("parseTalismanTier");
  if (section[0].startsWith(_$.TALISMAN_TIER)) {
    item.talismanTier = Number(section[0].slice(_$.TALISMAN_TIER.length));
    return "SECTION_PARSED";
  }
  return "SECTION_SKIPPED";
}

// disabling cause of https://github.com/Kvan7/Exiled-Exchange-2/issues/954
// will come back if vaal gems are added later, will need to be reworked completely
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function parseVaalGemName(section: string[], item: ParserState) {
  performance.mark("parseVaalGemName");
  if (item.category !== ItemCategory.Gem) return "PARSER_SKIPPED";

  // TODO blocked by https://www.pathofexile.com/forum/view-thread/3231236
  if (section.length === 1) {
    let gemName: string | undefined;
    if (ITEM_BY_REF("GEM", section[0])) {
      gemName = section[0];
    } else if (ITEM_BY_TRANSLATED("GEM", section[0])) {
      gemName = section[0];
    }
    if (gemName) {
      let gemItem = ITEM_BY_REF("GEM", gemName);
      if (!gemItem || !gemItem.length) {
        gemItem = ITEM_BY_TRANSLATED("GEM", gemName);
      }
      if (!gemItem || !gemItem.length) {
        return "SECTION_SKIPPED";
      }

      item.name = gemItem![0].refName;
      return "SECTION_PARSED";
    }
  }
  return "SECTION_SKIPPED";
}

function parseGem(section: string[], item: ParsedItem) {
  performance.mark("parseGem");
  if (
    item.category !== ItemCategory.Gem &&
    item.category !== ItemCategory.MetaGem &&
    item.category !== ItemCategory.UncutGem
  ) {
    return "PARSER_SKIPPED";
  }

  const gemLevelLineNumber =
    item.category === ItemCategory.Gem || item.category === ItemCategory.MetaGem
      ? 1
      : 0;

  if (section[gemLevelLineNumber]?.startsWith(_$.GEM_LEVEL)) {
    // "Level: 20 (Max)"
    item.gemLevel = parseInt(
      section[gemLevelLineNumber].slice(_$.GEM_LEVEL.length),
      10,
    );

    parseQualityNested(section, item);

    return "SECTION_PARSED";
  }
  return "SECTION_SKIPPED";
}
// #endregion

function parseStackSize(section: string[], item: ParsedItem) {
  performance.mark("parseStackSize");
  if (
    item.rarity !== ItemRarity.Normal &&
    item.category !== ItemCategory.Currency &&
    item.category !== ItemCategory.DivinationCard &&
    item.category !== ItemCategory.MapFragment
  ) {
    return "PARSER_SKIPPED";
  }
  if (section[0].startsWith(_$.STACK_SIZE)) {
    // Portal Scroll "Stack Size: 2[localized separator]448/40"
    const [value, max] = section[0]
      .slice(_$.STACK_SIZE.length)
      .replace(/[^\d/]/g, "")
      .split("/")
      .map(Number);
    if (item.info.refName !== "Idol of Estazunti") {
      item.stackSize = { value, max };
    }

    return "SECTION_PARSED";
  }
  return "SECTION_SKIPPED";
}

function parseAugmentSockets(section: string[], item: ParsedItem) {
  performance.mark("parseAugmentSockets");
  const categoryMax = getMaxSockets(item);

  if (!categoryMax) return "PARSER_SKIPPED";

  if (section[0].startsWith(_$.SOCKETS)) {
    const sockets = section[0].slice(_$.SOCKETS.length).trimEnd();
    const current = sockets.split("S").length - 1;
    if (!itemIsModifiable(item)) {
      item.augmentSockets = {
        empty: 0,
        current,
        normal: categoryMax,
        augments: Array(current).fill(null),
      };
    } else {
      item.augmentSockets = {
        empty: 0,
        current,
        normal: categoryMax,
        augments: Array(categoryMax > current ? categoryMax : current).fill(
          null,
        ),
      };
    }

    return "SECTION_PARSED";
  }
  // don't have any....possibly "yet"....
  if (categoryMax && itemIsModifiable(item)) {
    item.augmentSockets = {
      empty: categoryMax,
      current: 0,
      normal: categoryMax,
      augments: Array(categoryMax).fill(null),
    };
  }
  return "SECTION_SKIPPED";
}

function parseSockets(section: string[], item: ParsedItem) {
  performance.mark("parseSockets");
  if (
    item.category &&
    GEM.has(item.category) &&
    section[0].startsWith(_$.SOCKETS)
  ) {
    let sockets = section[0].slice(_$.SOCKETS.length).trimEnd();
    sockets = sockets.replace(/[^ -]/g, "#");

    item.gemSockets = {
      number: sockets.split("#").length - 1,
      white: sockets.split("W").length - 1,
      linked: undefined,
    };

    if (sockets === "#-#-#-#-#-#") {
      item.gemSockets.linked = 6;
    } else if (
      sockets === "# #-#-#-#-#" ||
      sockets === "#-#-#-#-# #" ||
      sockets === "#-#-#-#-#"
    ) {
      item.gemSockets.linked = 5;
    }
    return "SECTION_PARSED";
  }
  return "SECTION_SKIPPED";
}

function parseQualityNested(section: string[], item: ParsedItem) {
  performance.mark("parseQualityNested");
  for (const line of section) {
    if (line.startsWith(_$.QUALITY)) {
      // "Quality: +20% (augmented)"
      item.quality = parseInt(line.slice(_$.QUALITY.length), 10);

      if (
        item.category === ItemCategory.Ring ||
        item.category === ItemCategory.Amulet ||
        item.category === ItemCategory.Jewel
      ) {
        const catalysts =
          item.category === ItemCategory.Jewel
            ? CATALYST_TYPES.Refined
            : CATALYST_TYPES.Normal;
        for (const catalyst of catalysts) {
          for (const tag of CATALYST_TO_TAG[catalyst.tags[1]]) {
            if (line.includes(tag)) {
              item.qualityType = catalyst.tags[1];
              return;
            }
          }
        }
      }
      return;
    }
  }
}

function parseArmour(section: string[], item: ParsedItem) {
  performance.mark("parseArmour");
  let isParsed: SectionParseResult = "SECTION_SKIPPED";

  for (const line of section) {
    if (line.startsWith(_$.ARMOUR)) {
      item.armourAR = parseInt(line.slice(_$.ARMOUR.length), 10);
      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.EVASION)) {
      item.armourEV = parseInt(line.slice(_$.EVASION.length), 10);
      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.ENERGY_SHIELD)) {
      item.armourES = parseInt(line.slice(_$.ENERGY_SHIELD.length), 10);
      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.BLOCK_CHANCE)) {
      item.armourBLOCK = parseInt(line.slice(_$.BLOCK_CHANCE.length), 10);
      isParsed = "SECTION_PARSED";
      continue;
    }

    if (line.startsWith(_$.RUNIC_WARD)) {
      item.armourRW = parseInt(line.slice(_$.RUNIC_WARD.length), 10);
      isParsed = "SECTION_PARSED";
      continue;
    }
  }

  if (isParsed === "SECTION_PARSED") {
    parseQualityNested(section, item);
  }
  if (item.rarity === "Unique") {
    // undo everything
    item.armourAR = undefined;
    item.armourEV = undefined;
    item.armourES = undefined;
    item.armourRW = undefined; // ? maybe not ?
    item.armourBLOCK = undefined;
  }

  return isParsed;
}

function parseWeapon(section: string[], item: ParsedItem) {
  performance.mark("parseWeapon");
  let isParsed: SectionParseResult = "SECTION_SKIPPED";

  for (const line of section) {
    if (line.startsWith(_$.CRIT_CHANCE)) {
      // No regex since it can have decimals
      item.weaponCRIT = parseFloat(line.slice(_$.CRIT_CHANCE.length));
      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.ATTACK_SPEED)) {
      // No regex since it can have decimals
      item.weaponAS = parseFloat(line.slice(_$.ATTACK_SPEED.length));
      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.PHYSICAL_DAMAGE)) {
      item.weaponPHYSICAL = getRollOrMinmaxAvg(
        line
          .slice(_$.PHYSICAL_DAMAGE.length)
          .split(_$.HYPHEN)
          .map((str) => parseInt(str.replace(/[^\d]/g, ""), 10)),
      );
      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.ELEMENTAL_DAMAGE)) {
      item.weaponELEMENTAL = line
        .slice(_$.ELEMENTAL_DAMAGE.length)
        .split(", ")
        .map((element) =>
          getRollOrMinmaxAvg(
            element
              .split(_$.HYPHEN)
              .map((str) => parseInt(str.replace(/[^\d]/g, ""), 10)),
          ),
        )
        .reduce((sum, x) => sum + x, 0);

      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.FIRE_DAMAGE)) {
      const fireDamage = line
        .slice(_$.FIRE_DAMAGE.length)
        .split(", ")
        .map((element) =>
          getRollOrMinmaxAvg(
            element
              .split(_$.HYPHEN)
              .map((str) => parseInt(str.replace(/[^\d]/g, ""), 10)),
          ),
        )
        .reduce((sum, x) => sum + x, 0);
      if (item.weaponELEMENTAL) {
        item.weaponELEMENTAL = fireDamage + item.weaponELEMENTAL;
      } else {
        item.weaponELEMENTAL = fireDamage;
      }
      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.COLD_DAMAGE)) {
      const coldDamage = line
        .slice(_$.COLD_DAMAGE.length)
        .split(", ")
        .map((element) =>
          getRollOrMinmaxAvg(
            element
              .split(_$.HYPHEN)
              .map((str) => parseInt(str.replace(/[^\d]/g, ""), 10)),
          ),
        )
        .reduce((sum, x) => sum + x, 0);
      if (item.weaponELEMENTAL) {
        item.weaponELEMENTAL = coldDamage + item.weaponELEMENTAL;
      } else {
        item.weaponELEMENTAL = coldDamage;
      }
      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.LIGHTNING_DAMAGE)) {
      const lightningDamage = line
        .slice(_$.LIGHTNING_DAMAGE.length)
        .split(", ")
        .map((element) =>
          getRollOrMinmaxAvg(
            element
              .split(_$.HYPHEN)
              .map((str) => parseInt(str.replace(/[^\d]/g, ""), 10)),
          ),
        )
        .reduce((sum, x) => sum + x, 0);
      if (item.weaponELEMENTAL) {
        item.weaponELEMENTAL = lightningDamage + item.weaponELEMENTAL;
      } else {
        item.weaponELEMENTAL = lightningDamage;
      }
      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.RELOAD_SPEED)) {
      // No regex since it can have decimals
      item.weaponRELOAD = parseFloat(line.slice(_$.RELOAD_SPEED.length));
      isParsed = "SECTION_PARSED";
      continue;
    }
    if (line.startsWith(_$.BASE_SPIRIT)) {
      item.weaponSPIRIT = parseInt(line.slice(_$.BASE_SPIRIT.length), 10);
      isParsed = "SECTION_PARSED";
      continue;
    }
  }

  if (isParsed === "SECTION_PARSED") {
    parseQualityNested(section, item);
  }

  if (item.rarity === "Unique") {
    // undo everything
    item.weaponELEMENTAL = undefined;
    item.weaponAS = undefined;
    item.weaponPHYSICAL = undefined;
    item.weaponCOLD = undefined;
    item.weaponLIGHTNING = undefined;
    item.weaponFIRE = undefined;
    item.weaponCRIT = undefined;
    item.weaponRELOAD = undefined;
    item.weaponSPIRIT = undefined;
  }

  return isParsed;
}

function parseCaster(section: string[], item: ParsedItem) {
  performance.mark("parseCaster");
  if (
    item.category !== ItemCategory.Wand &&
    item.category !== ItemCategory.Sceptre &&
    item.category !== ItemCategory.Staff
  )
    return "PARSER_SKIPPED";

  if (section.length === 1 && section[0].startsWith(_$.QUALITY)) {
    parseQualityNested(section, item);
    return "SECTION_PARSED";
  }

  return "SECTION_SKIPPED";
}

function parseLogbookArea(section: string[], item: ParsedItem) {
  performance.mark("parseLogbookArea");
  if (item.info.refName !== "Expedition Logbook") return "PARSER_SKIPPED";
  if (section.length < 3) return "SECTION_SKIPPED";

  // skip Area, parse Faction
  const faction = STAT_BY_MATCH_STR(section[1]);
  if (!faction) return "SECTION_SKIPPED";

  const areaMods: ParsedModifier[] = [
    {
      info: { tags: [], type: ModifierType.Pseudo },
      stats: [
        {
          stat: faction.stat,
          translation: faction.matcher,
        },
      ],
    },
  ];

  const { modType, lines } = parseModType(section.slice(2));
  for (const line of lines) {
    const found = tryParseTranslation(
      { string: line, unscalable: false },
      modType,
      undefined,
    );
    if (found) {
      areaMods.push({
        info: { tags: [], type: modType },
        stats: [found],
      });
    }
  }

  areaMods.shift();
  if (areaMods.length) {
    if (!item.logbookAreaMods) {
      item.logbookAreaMods = [areaMods];
    } else {
      item.logbookAreaMods.push(areaMods);
    }
  }

  return "SECTION_PARSED";
}

export function parseModifiersPoe2(section: string[], item: ParsedItem) {
  performance.mark("parseModifiersPoe2");
  if (
    item.rarity !== ItemRarity.Normal &&
    item.rarity !== ItemRarity.Magic &&
    item.rarity !== ItemRarity.Rare &&
    item.rarity !== ItemRarity.Unique
  ) {
    return "PARSER_SKIPPED";
  }

  let foundAnyMods = false;

  const hasEndingTag = section.find(
    (line) =>
      line.endsWith(ENCHANT_LINE) ||
      line.endsWith(SCOURGE_LINE) ||
      line.endsWith(AUGMENT_LINE) ||
      line.endsWith(ADDED_AUGMENT_LINE) ||
      line.startsWith(_$.GRANTS_SKILL),
  );

  if (hasEndingTag) {
    const { lines } = parseModType(section);
    let modType;

    if (hasEndingTag.endsWith(ENCHANT_LINE)) {
      modType = ModifierType.Enchant;
    } else if (hasEndingTag.endsWith(SCOURGE_LINE)) {
      modType = ModifierType.Scourge;
    } else if (hasEndingTag.endsWith(ADDED_AUGMENT_LINE)) {
      modType = ModifierType.AddedAugment;
    } else if (hasEndingTag.endsWith(AUGMENT_LINE)) {
      modType = ModifierType.Augment;
    } else if (hasEndingTag.startsWith(_$.GRANTS_SKILL)) {
      modType = ModifierType.Skill;
    } else {
      throw new Error("Invalid ending tag");
    }
    const modInfo: ModifierInfo = {
      type: modType,
      tags: [],
    };
    foundAnyMods = parseStatsFromMod(lines, item, { info: modInfo, stats: [] });
  } else {
    for (const statLines of section) {
      let { modType, lines } = parseModType([statLines]);
      if (
        modType === ModifierType.Explicit &&
        item.category === ItemCategory.Relic
      ) {
        modType = ModifierType.Sanctum;
      }
      // const modInfo = parseModInfoLine(modLine, modType);
      const found = parseStatsFromMod(lines, item, {
        info: { type: modType, tags: [] },
        stats: [],
      });
      foundAnyMods = found || foundAnyMods;

      if (modType === ModifierType.Veiled) {
        item.isVeiled = true;
      }
    }
  }

  return foundAnyMods ? "SECTION_PARSED" : "SECTION_SKIPPED";
}

function parseModifiers(section: string[], item: ParsedItem) {
  performance.mark("parseModifiers");
  if (
    item.rarity !== ItemRarity.Normal &&
    item.rarity !== ItemRarity.Magic &&
    item.rarity !== ItemRarity.Rare &&
    item.rarity !== ItemRarity.Unique
  ) {
    return "PARSER_SKIPPED";
  }

  const recognizedLine = section.find(
    (line) =>
      line.endsWith(ENCHANT_LINE) ||
      line.endsWith(AUGMENT_LINE) ||
      line.startsWith(_$.GRANTS_SKILL) ||
      isModInfoLine(line),
  );

  if (!recognizedLine) {
    return "SECTION_SKIPPED";
  }

  if (isModInfoLine(recognizedLine)) {
    for (const { modLine, statLines } of groupLinesByMod(section)) {
      const { modType, lines } = parseModType(statLines);

      const modInfo = parseModInfoLine(modLine, modType);
      if (
        item.category === ItemCategory.Relic &&
        modInfo.type === ModifierType.Explicit
      ) {
        modInfo.type = ModifierType.Sanctum;
      }
      parseStatsFromMod(lines, item, { info: modInfo, stats: [] });

      if (modType === ModifierType.Veiled) {
        item.isVeiled = true;
      }
    }
  } else {
    const { lines } = parseModType(section);
    const modInfo: ModifierInfo = {
      type: recognizedLine.endsWith(ENCHANT_LINE)
        ? ModifierType.Enchant
        : recognizedLine.startsWith(_$.GRANTS_SKILL)
          ? ModifierType.Skill
          : ModifierType.Augment,
      tags: [],
    };
    parseStatsFromMod(lines, item, { info: modInfo, stats: [] });
  }

  return "SECTION_PARSED";
}

function applyAugmentSockets(item: ParsedItem) {
  performance.mark("applyAugmentSockets");
  // If we have any augment sockets
  if (item.augmentSockets) {
    // Count current mods that are of type Augment
    const augmentMods = item.newMods.filter(
      (mod) => mod.info.type === ModifierType.Augment,
    );
    const augmentStats = item.statsByType
      .filter((calc) => calc.type === ModifierType.Augment)
      .filter((calc) => !calc.stat.ref.startsWith("Bonded"));

    let statCombinations = combinations(augmentStats)
      .filter((f) => f.length)
      .toSorted((a, b) => b.length - a.length);
    const augs: BaseType[][] = [];
    for (let i = 0; i < statCombinations.length; ) {
      const foundAugs = determineAugments(augmentMods[0], statCombinations[i]);
      if (foundAugs.length) {
        augs.push(foundAugs);
        for (const combStat of statCombinations[i]) {
          // drop any that we use here
          statCombinations = statCombinations.filter(
            (f) => f.find((s) => s === combStat) === undefined,
          );
        }
        i = 0;
        continue;
      }
      i++;
    }

    const augments: Array<EditorItem | null> = augs
      .map((augGroup) => {
        return buildEditorItems(
          augGroup,
          item.category ?? ItemCategory.Unknown,
          true,
        );
      })
      .flat();

    if (
      augments.length <
      Math.max(item.augmentSockets.current, item.augmentSockets.normal)
    ) {
      // have possible empty sockets

      const emptyAugments = item.isCorrupted
        ? item.augmentSockets.current - augments.length
        : Math.max(item.augmentSockets.current, item.augmentSockets.normal) -
          augments.length;
      for (let i = 0; i < emptyAugments; i++) {
        augments.push(null);
      }
    }

    // set all the stuff

    item.augmentSockets.empty = augments.filter(
      (augment) => augment === null,
    ).length;
    item.augmentSockets.augments = augments;
  }

  if (
    item.augmentSockets &&
    (item.augmentSockets.empty === item.augmentSockets.normal ||
      (item.augmentSockets.empty === item.augmentSockets.current &&
        item.augmentSockets.current > item.augmentSockets.normal)) &&
    item.rarity !== ItemRarity.Unique
  ) {
    // fill sockets, if all are empty && if chosen (also maybe check setting to enable it)
    const augmentBases = getSavedAugments(item);
    const editorItems = augmentBases.map((base) => {
      if (!base) return null;
      return buildEditorItems([base], item.category ?? ItemCategory.Unknown)[0];
    });
    if (!editorItems.length || editorItems.every((item) => item === null)) {
      return;
    }
    // fill the correct number of sockets
    const allSameAugment = new Set(augmentBases).size === 1;
    const augmentCountToApply =
      itemIsModifiable(item) &&
      item.augmentSockets.normal > item.augmentSockets.current
        ? item.augmentSockets.normal
        : item.augmentSockets.current;

    if (allSameAugment) {
      const augmentToApply = editorItems[0];
      for (let i = 0; i < augmentCountToApply; i++) {
        useAugment(item, augmentToApply!, i);
      }
    } else {
      for (let i = 0; i < augmentCountToApply; i++) {
        if (!editorItems[i]) continue;
        useAugment(item, editorItems[i]!, i);
      }
    }
  }
}

function parseMirrored(section: string[], item: ParsedItem) {
  performance.mark("parseMirrored");
  if (section.length === 1) {
    if (section[0] === _$.MIRRORED) {
      item.isMirrored = true;
      return "SECTION_PARSED";
    }
  }
  return "SECTION_SKIPPED";
}

function parseSanctified(section: string[], item: ParsedItem) {
  performance.mark("parseSanctified");
  if (section.length === 1) {
    if (section[0] === _$.SANCTIFIED) {
      item.isSanctified = true;
      return "SECTION_PARSED";
    }
  }
  return "SECTION_SKIPPED";
}

function parseFlask(section: string[], item: ParsedItem) {
  performance.mark("parseFlask");
  // the purpose of this parser is to "consume" flask buffs
  // so they are not recognized as modifiers

  let isParsed: SectionParseResult = "SECTION_SKIPPED";

  for (const line of section) {
    if (_$.FLASK_CHARGES.test(line)) {
      isParsed = "SECTION_PARSED";
      break;
    }
  }

  if (isParsed) {
    parseQualityNested(section, item);
  }

  return isParsed;
}

function parseJewelery(section: string[], item: ParsedItem) {
  performance.mark("parseJewelery");
  if (
    item.category !== ItemCategory.Amulet &&
    item.category !== ItemCategory.Ring &&
    item.category !== ItemCategory.Belt
  ) {
    return "PARSER_SKIPPED";
  }

  for (const line of section) {
    if (line.startsWith(_$.QUALITY.substring(0, _$.QUALITY.indexOf(":")))) {
      return "SECTION_PARSED";
    }
  }

  return "SECTION_SKIPPED";
}

function parseCharmSlots(section: string[], item: ParsedItem) {
  performance.mark("parseCharmSlots");
  // the purpose of this parser is to "consume" charm slot 1 sections
  // so they are not recognized as modifiers
  if (item.category !== ItemCategory.Belt) return "PARSER_SKIPPED";

  let isParsed: SectionParseResult = "SECTION_SKIPPED";

  for (const line of section) {
    if (line.startsWith(_$.CHARM_SLOTS)) {
      isParsed = "SECTION_PARSED";
      break;
    }
  }

  return isParsed;
}

function parseSpirit(section: string[], item: ParsedItem) {
  performance.mark("parseSpirit");
  // the purpose of this parser is to "consume" Spirit: 100 sections
  // so they are not recognized as modifiers
  if (item.category !== ItemCategory.Sceptre) return "PARSER_SKIPPED";

  let isParsed: SectionParseResult = "SECTION_SKIPPED";

  for (const line of section) {
    if (line.startsWith(_$.BASE_SPIRIT)) {
      isParsed = "SECTION_PARSED";
      break;
    }
  }

  return isParsed;
}

function parsePriceNote(section: string[], item: ParsedItem) {
  performance.mark("parsePriceNote");
  for (const line of section) {
    if (line.startsWith(_$.PRICE_NOTE)) {
      item.note = line.slice(_$.PRICE_NOTE.length);
      return "SECTION_PARSED";
    }
  }

  return "SECTION_SKIPPED";
}

function parseTimelostRadius(section: string[], item: ParsedItem) {
  performance.mark("parseTimelostRadius");
  if (item.category !== ItemCategory.Jewel) return "PARSER_SKIPPED";
  for (const line of section) {
    if (line.startsWith(_$.TIMELESS_RADIUS)) {
      return "SECTION_PARSED";
    }
  }
  return "SECTION_SKIPPED";
}

function parseSentinelCharge(section: string[], item: ParsedItem) {
  performance.mark("parseSentinelCharge");
  if (item.category !== ItemCategory.Sentinel) return "PARSER_SKIPPED";

  if (section.length === 1) {
    if (section[0].startsWith(_$.SENTINEL_CHARGE)) {
      item.sentinelCharge = parseInt(
        section[0].slice(_$.SENTINEL_CHARGE.length),
        10,
      );
      return "SECTION_PARSED";
    }
  }
  return "SECTION_SKIPPED";
}

function parseSynthesised(section: string[], item: ParserState) {
  performance.mark("parseSynthesised");
  if (section.length === 1) {
    if (section[0] === _$.SECTION_SYNTHESISED) {
      item.isSynthesised = true;
      if (item.baseType) {
        let baseTypeReg = _$REF.ITEM_SYNTHESISED.exec(item.baseType);
        if (!baseTypeReg || baseTypeReg.length < 2) {
          baseTypeReg = _$.ITEM_SYNTHESISED.exec(item.baseType);
        }
        item.baseType = baseTypeReg![1];
      } else {
        let nameReg = _$REF.ITEM_SYNTHESISED.exec(item.name);
        if (!nameReg || nameReg.length < 2) {
          nameReg = _$.ITEM_SYNTHESISED.exec(item.name);
        }
        item.name = nameReg![1];
      }
      return "SECTION_PARSED";
    }
  }

  return "SECTION_SKIPPED";
}

function parseSuperior(item: ParserState) {
  performance.mark("parseSuperior");
  if (
    item.rarity === ItemRarity.Normal ||
    (item.rarity === ItemRarity.Magic && item.isUnidentified) ||
    (item.rarity === ItemRarity.Rare && item.isUnidentified) ||
    (item.rarity === ItemRarity.Unique && item.isUnidentified)
  ) {
    if (_$REF.ITEM_SUPERIOR.test(item.name)) {
      item.name = _$REF.ITEM_SUPERIOR.exec(item.name)![1];
    } else if (_$.ITEM_SUPERIOR.test(item.name)) {
      item.name = _$.ITEM_SUPERIOR.exec(item.name)![1];
    }
  }
}

function parseExceptional(item: ParserState) {
  performance.mark("parseExceptional");
  if (
    item.rarity === ItemRarity.Normal ||
    (item.rarity === ItemRarity.Magic && item.isUnidentified) ||
    (item.rarity === ItemRarity.Rare && item.isUnidentified) ||
    (item.rarity === ItemRarity.Unique && item.isUnidentified)
  ) {
    if (_$REF.ITEM_EXCEPTIONAL.test(item.name)) {
      item.name = _$REF.ITEM_EXCEPTIONAL.exec(item.name)![1];
    } else if (_$.ITEM_EXCEPTIONAL.test(item.name)) {
      item.name = _$.ITEM_EXCEPTIONAL.exec(item.name)![1];
    }
  }
}

function parseRuneforged(item: ParserState) {
  performance.mark("parseRuneforged");
  if (
    item.rarity === ItemRarity.Normal ||
    item.rarity === ItemRarity.Magic ||
    item.rarity === ItemRarity.Rare ||
    item.rarity === ItemRarity.Unique
  ) {
    // NOTE: don't actually use since they different basetype
    // if (_$REF.ITEM_RUNEFORGED.test(item.name)) {
    //   item.name = _$REF.ITEM_RUNEFORGED.exec(item.name)![1];
    // }
  }
}

function parseCategoryByHelpText(section: string[], item: ParsedItem) {
  performance.mark("parseCategoryByHelpText");
  if (section[0] === _$.BEAST_HELP) {
    item.category = ItemCategory.CapturedBeast;
    return "SECTION_PARSED";
  } else if (section[0] === _$.METAMORPH_HELP) {
    item.category = ItemCategory.MetamorphSample;
    return "SECTION_PARSED";
  } else if (section[0] === _$.VOIDSTONE_HELP) {
    item.category = ItemCategory.Voidstone;
    return "SECTION_PARSED";
  }

  return "SECTION_SKIPPED";
}

function parseHeistBlueprint(section: string[], item: ParsedItem) {
  performance.mark("parseHeistBlueprint");
  if (item.category !== ItemCategory.HeistBlueprint) return "PARSER_SKIPPED";

  parseAreaLevelNested(section, item);
  if (!item.areaLevel) {
    return "SECTION_SKIPPED";
  }

  item.heist = {};

  for (const line of section) {
    if (line.startsWith(_$.HEIST_TARGET)) {
      const targetText = line.slice(_$.HEIST_TARGET.length);
      switch (targetText) {
        case _$.HEIST_BLUEPRINT_ENCHANTS:
          item.heist.target = "Enchants";
          break;
        case _$.HEIST_BLUEPRINT_GEMS:
          item.heist.target = "Gems";
          break;
        case _$.HEIST_BLUEPRINT_REPLICAS:
          item.heist.target = "Replicas";
          break;
        case _$.HEIST_BLUEPRINT_TRINKETS:
          item.heist.target = "Trinkets";
          break;
      }
    } else if (line.startsWith(_$.HEIST_WINGS_REVEALED)) {
      item.heist.wingsRevealed = parseInt(
        line.slice(_$.HEIST_WINGS_REVEALED.length),
        10,
      );
    }
  }

  return "SECTION_PARSED";
}

function parseTrials(section: string[], item: ParsedItem) {
  performance.mark("parseTrials");
  if (
    item.info.refName !== "Djinn Barya" &&
    item.info.refName !== "Inscribed Ultimatum"
  ) {
    return "PARSER_SKIPPED";
  }

  parseAreaLevelNested(section, item);
  if (!item.areaLevel) {
    return "SECTION_SKIPPED";
  }
  item.trials = {};

  for (const line of section) {
    if (line.startsWith(_$.TRIAL_COUNT)) {
      item.trials.numberOfTrials = Number(line.slice(_$.TRIAL_COUNT.length));
    } else if (item.info.refName === "Inscribed Ultimatum") {
      if (line.startsWith(_$.ULTIMATUM_VICTORIOUS)) {
        item.trials.ultimatumHint = "Victorious";
      } else if (line.startsWith(_$.ULTIMATUM_COWARDLY)) {
        item.trials.ultimatumHint = "Cowardly";
      } else if (line.startsWith(_$.ULTIMATUM_DEADLY)) {
        item.trials.ultimatumHint = "Deadly";
      }
    }
  }

  return "SECTION_PARSED";
}

function parseAreaLevelNested(section: string[], item: ParsedItem) {
  performance.mark("parseAreaLevelNested");
  for (const line of section) {
    if (line.startsWith(_$.AREA_LEVEL)) {
      item.areaLevel = Number(line.slice(_$.AREA_LEVEL.length));
      break;
    }
  }
}

function parseAreaLevel(section: string[], item: ParsedItem) {
  performance.mark("parseAreaLevel");
  if (
    item.info.refName !== "Chronicle of Atzoatl" &&
    item.info.refName !== "Expedition Logbook" &&
    item.info.refName !== "Mirrored Tablet" &&
    item.info.refName !== "Forbidden Tome"
  )
    return "PARSER_SKIPPED";

  parseAreaLevelNested(section, item);

  return item.areaLevel ? "SECTION_PARSED" : "SECTION_SKIPPED";
}

function parseAtzoatlRooms(section: string[], item: ParsedItem) {
  performance.mark("parseAtzoatlRooms");
  if (item.info.refName !== "Chronicle of Atzoatl") return "PARSER_SKIPPED";
  if (section[0] !== _$.INCURSION_OPEN) return "SECTION_SKIPPED";

  let state = IncursionRoom.Open;
  for (const line of section.slice(1)) {
    if (line === _$.INCURSION_OBSTRUCTED) {
      state = IncursionRoom.Obstructed;
      continue;
    }

    const found = STAT_BY_MATCH_STR(line);
    if (found) {
      item.newMods.push({
        info: { tags: [], type: ModifierType.Pseudo },
        stats: [
          {
            stat: found.stat,
            translation: {
              string:
                state === IncursionRoom.Open
                  ? found.matcher.string
                  : `${_$.INCURSION_OBSTRUCTED} ${found.matcher.string}`,
            },
            roll: {
              value: state,
              min: state,
              max: state,
              dp: false,
              unscalable: true,
            },
          },
        ],
      });
    } else {
      item.unknownModifiers.push({
        text: line,
        type: ModifierType.Pseudo,
      });
    }
  }

  return "SECTION_PARSED";
}

function parseMirroredTablet(section: string[], item: ParsedItem) {
  performance.mark("parseMirroredTablet");
  if (item.info.refName !== "Mirrored Tablet") return "PARSER_SKIPPED";
  if (section.length < 8) return "SECTION_SKIPPED";

  for (const line of section) {
    const found = tryParseTranslation(
      { string: line, unscalable: true },
      ModifierType.Pseudo,
      undefined,
    );
    if (found) {
      item.newMods.push({
        info: { tags: [], type: ModifierType.Pseudo },
        stats: [found],
      });
    } else {
      item.unknownModifiers.push({
        text: line,
        type: ModifierType.Pseudo,
      });
    }
  }

  return "SECTION_PARSED";
}

function parseFilledCoffin(section: string[], item: ParsedItem) {
  performance.mark("parseFilledCoffin");
  if (item.info.refName !== "Filled Coffin") return "PARSER_SKIPPED";
  if (!section.some((line) => line.endsWith(IMPLICIT_LINE)))
    return "SECTION_SKIPPED";

  const { lines } = parseModType(section);
  const modInfo: ModifierInfo = {
    type: ModifierType.Necropolis,
    tags: [],
  };
  parseStatsFromMod(lines, item, { info: modInfo, stats: [] });

  return "SECTION_PARSED";
}

function markupConditionParser(text: string) {
  // ignores state set by <<set:__>>
  // always evaluates first condition to true <if:__>{...}
  // full markup: https://gist.github.com/SnosMe/151549b532df8ea08025a76ae2920ca4

  text = text.replace(/<<set:.+?>>/g, "");
  text = text.replace(
    /<(if:.+?|elif:.+?|else)>{(.+?)}/g,
    (_, type: string, body: string) => {
      return type.startsWith("if:") ? body : "";
    },
  );

  return text;
}

export function parseStatsFromMod(
  lines: string[],
  item: ParsedItem,
  modifier: ParsedModifier,
  addedAugment?: BaseType,
): boolean {
  item.newMods.push(modifier);

  if (modifier.info.type === ModifierType.Veiled) {
    return true;
  }

  const statIterator = linesToStatStrings(lines);
  let stat = statIterator.next();
  while (!stat.done) {
    if (item.info.refName === "From Nothing") {
      stat.value.string = stat.value.string.replace("()", "");
    }

    const parsedStat = tryParseTranslation(
      stat.value,
      modifier.info.type,
      item.category,
    );

    // fully skip builtin skills
    if (
      parsedStat?.stat.ref === "Grants Skill: Parry" ||
      parsedStat?.stat.ref === "Grants Skill: Raise Shield" ||
      parsedStat?.stat.ref === "Grants Skill: Spear Throw"
    ) {
      stat = statIterator.next(true);
      continue;
    }

    // const validTradeIds = parsedStat?.stat.trade.ids[
    //   modifier.info.type
    // ]?.filter((id) =>
    //   TRADE_STAT_BY_STAT_ID(
    //     id +
    //       (parsedStat.stat.trade.option
    //         ? "|" + parsedStat.translation.value
    //         : ""),
    //   ),
    // );

    // if (parsedStat && validTradeIds && validTradeIds.length) {
    if (parsedStat) {
      // keep augment basetype on the stat
      if (modifier.info.type === ModifierType.AddedAugment) {
        parsedStat.fromAddedAugment = addedAugment;
      }
      modifier.stats.push(parsedStat);

      stat = statIterator.next(true);
    } else {
      stat = statIterator.next(false);
    }
  }

  item.unknownModifiers.push(
    ...stat.value.map((line) => ({
      text: line,
      type: modifier.info.type,
    })),
  );

  return true;
}

/**
 * @deprecated
 */
function transformToLegacyModifiers(item: ParsedItem) {
  performance.mark("transformToLegacyModifiers");
  item.statsByType = sumStatsByModType(item.newMods);
}

function applyElementalAdded(item: ParsedItem) {
  if (item.weaponELEMENTAL && item.rarity !== "Unique") {
    const knownRefs = new Set<string>([
      "Adds # to # Lightning Damage",
      "Adds # to # Cold Damage",
      "Adds # to # Fire Damage",
    ]);

    item.statsByType.forEach((calc) => {
      if (knownRefs.has(calc.stat.ref)) {
        for (const source of calc.sources) {
          if (calc.stat.ref === "Adds # to # Lightning Damage") {
            if (item.weaponLIGHTNING) {
              item.weaponLIGHTNING =
                source.contributes!.value + item.weaponLIGHTNING;
            } else {
              item.weaponLIGHTNING = source.contributes!.value;
            }
          } else if (calc.stat.ref === "Adds # to # Cold Damage") {
            if (item.weaponCOLD) {
              item.weaponCOLD = source.contributes!.value + item.weaponCOLD;
            } else {
              item.weaponCOLD = source.contributes!.value;
            }
          } else if (calc.stat.ref === "Adds # to # Fire Damage") {
            if (item.weaponFIRE) {
              item.weaponFIRE = source.contributes!.value + item.weaponFIRE;
            } else {
              item.weaponFIRE = source.contributes!.value;
            }
          }
        }
      }
    });
  }
}

function calcBasePercentile(item: ParsedItem) {
  performance.mark("calcBasePercentile");
  // use ref since we have ref from unique.base
  const info = item.info.unique
    ? ITEM_BY_REF("ITEM", item.info.unique.base)![0].armour
    : item.info.armour;
  if (!info) return;

  // Base percentile is the same for all defences.
  // Using `AR/EV -> ES` order to improve accuracy
  // of calculation (larger rolls = more precise).
  if (item.armourAR && info.ar) {
    item.basePercentile = calcPropPercentile(
      item.armourAR,
      info.ar,
      QUALITY_STATS.ARMOUR,
      item,
    );
  } else if (item.armourEV && info.ev) {
    item.basePercentile = calcPropPercentile(
      item.armourEV,
      info.ev,
      QUALITY_STATS.EVASION,
      item,
    );
  } else if (item.armourES && info.es) {
    item.basePercentile = calcPropPercentile(
      item.armourES,
      info.es,
      QUALITY_STATS.ENERGY_SHIELD,
      item,
    );
  }
  // no ward since base percent isn't used anymore
}

export function removeLinesEnding(
  lines: readonly string[],
  ending: string,
): string[] {
  return lines.map((line) =>
    line.endsWith(ending) ? line.slice(0, -ending.length) : line,
  );
}

export function parseAffixStrings(text: string): string {
  return text.replace(/\[([^\]|]+)\|?([^\]]*)\]/g, (_, part1, part2) => {
    return part2 || part1;
  });
}
export function getMaxSockets(item: ParsedItem) {
  switch (item.info.refName) {
    case "Atziri's Splendour":
      return 6;

    case "Runeseeker's Call":
      return 5;

    case "Greymake":
    case "Morior Invictus":
    case "The Bringer of Rain":
      return 4;

    case "Serle's Grit":
      return 3;

    case "Darkness Enthroned":
    case "Mahuxotl's Machination":
      return 2;

    case "Grasping Ring":
    case "Corona Amulet":
    case "Stalking Belt":
      return 1;

    default:
    // fall through to below
  }

  const { category } = item;
  switch (category) {
    case ItemCategory.BodyArmour:
    case ItemCategory.TwoHandedAxe:
    case ItemCategory.TwoHandedMace:
    case ItemCategory.TwoHandedSword:
    case ItemCategory.Crossbow:
    case ItemCategory.Bow:
    case ItemCategory.Warstaff:
    case ItemCategory.Staff:
    case ItemCategory.Talisman:
      return 2;
    case ItemCategory.Helmet:
    case ItemCategory.Shield:
    case ItemCategory.Gloves:
    case ItemCategory.Boots:
    case ItemCategory.OneHandedAxe:
    case ItemCategory.OneHandedMace:
    case ItemCategory.OneHandedSword:
    case ItemCategory.Claw:
    case ItemCategory.Dagger:
    case ItemCategory.Focus:
    case ItemCategory.Spear:
    case ItemCategory.Flail:
    case ItemCategory.Wand:
    case ItemCategory.Buckler:
    case ItemCategory.Sceptre:
      return 1;
    default:
      return 0;
  }
}

export function isArmourOrWeaponOrCaster(
  category: ItemCategory | undefined,
): "armour" | "weapon" | "caster" | undefined {
  switch (category) {
    case ItemCategory.BodyArmour:
    case ItemCategory.Boots:
    case ItemCategory.Gloves:
    case ItemCategory.Helmet:
    case ItemCategory.Shield:
    case ItemCategory.Focus:
    case ItemCategory.Buckler:
      return "armour";
    case ItemCategory.OneHandedAxe:
    case ItemCategory.OneHandedMace:
    case ItemCategory.OneHandedSword:
    case ItemCategory.Quiver:
    case ItemCategory.Claw:
    case ItemCategory.Dagger:
    case ItemCategory.Sceptre:
    case ItemCategory.TwoHandedAxe:
    case ItemCategory.TwoHandedMace:
    case ItemCategory.TwoHandedSword:
    case ItemCategory.Crossbow:
    case ItemCategory.Bow:
    case ItemCategory.Warstaff:
    case ItemCategory.Spear:
    case ItemCategory.Flail:
    case ItemCategory.Talisman:
      return "weapon";
    case ItemCategory.Wand:
    case ItemCategory.Staff:
      return "caster";
    default:
      return undefined;
  }
}

function modifiedBfs(
  remaining: number,
  combo: number[],
  available: number[],
): number[] | null {
  if (remaining === 0) return [...combo];
  if (combo.length >= 7 || available.length > 8) return [];

  let best: number[] | null = null;
  for (const augValue of available) {
    if (augValue > remaining) {
      // overflow
      continue;
    }
    combo.push(augValue);
    const result = modifiedBfs(remaining - augValue, combo, available);
    combo.pop();
    if (result) {
      if (!best || result.length < best.length) {
        best = result;
      } else if (result.length === best.length) {
        const resultMax = Math.max(
          ...result.map((v) => result.filter((x) => x === v).length),
        );
        const bestMax = Math.max(
          ...best.map((v) => best!.filter((x) => x === v).length),
        );
        if (resultMax > bestMax) {
          best = result;
        } else if (resultMax === bestMax) {
          const resultSet = new Set(result);
          const bestSet = new Set(best);
          if (resultSet.size < bestSet.size) {
            // use one with more duplicates
            best = result;
          }
        }
      }
    }
  }
  return best?.toSorted((a, b) => b - a) ?? null;
}

function determineAugments(
  mod: ParsedModifier,
  statCalcs: StatCalculated[],
): BaseType[] {
  if (mod.info.type !== ModifierType.Augment) return [];
  const augmentAppliedValue = statCalcs
    .map((s) => s.sources[0].contributes?.value)
    .filter((v) => v !== undefined);

  // const augmentTradeId = statCalcs[0].stat.trade.ids[ModifierType.Augment][0];
  // const possibleAugments = AUGMENT_DATA_BY_TRADE_ID[augmentTradeId];

  const allTradeIds = statCalcs.map(
    (c) => c.stat.trade.ids[ModifierType.Augment][0],
  );
  const allPossibleAugments = allTradeIds
    .map((id) => AUGMENT_DATA_BY_TRADE_ID[id])
    .filter((v) => v !== undefined);

  const augmentRefSets = allPossibleAugments.map(
    (augGroup) => new Set(augGroup.map((a) => a.refName)),
  );

  const morePossibleAugments = augmentRefSets.reduce((acc, set) => {
    return set.intersection(acc);
  }, augmentRefSets[0]);

  // intersect all possible augments
  const possibleAugments = allPossibleAugments
    .map((augGroup) =>
      augGroup.filter((aug) => morePossibleAugments.has(aug.refName)),
    )
    .find((f) => f.length);

  if (!possibleAugments) return [];

  // something like "Raven-Touched"
  if (!augmentAppliedValue || !augmentAppliedValue.length) {
    const singleAugment = possibleAugments[0];
    return [ITEM_BY_REF("ITEM", singleAugment.refName)![0]];
  }

  if (augmentAppliedValue.length > 1) {
    const likelyAugment = possibleAugments.find(
      (aug) =>
        new Set(aug.values).intersection(new Set(augmentAppliedValue)).size ===
        augmentAppliedValue.length,
    );
    if (!likelyAugment) return [];
    return [ITEM_BY_REF("ITEM", likelyAugment.refName)![0]];
  }

  // // Calculate how many of this augment are in the item
  const availableAugmentValues = possibleAugments
    .map((augment) => {
      if (augment.values.length === 1) return augment.values[0];

      // stats like "# to # added Lightning Damage"
      if ((augment.baseStat.match(/#/g) || []).length > 1) {
        const sum = augment.values.reduce((a, b) => a + b, 0);
        return sum / augment.values.length || 0;
      }

      // everything else
      return augment.values[0];
    })
    .toSorted((a, b) => b - a);

  // BFS to find all combinations with minimum count
  const likelyValues =
    modifiedBfs(augmentAppliedValue[0], [], availableAugmentValues) ?? [];

  return likelyValues.map((v) => {
    const augment = possibleAugments.find(
      (aug) =>
        aug.values.some((augVal) => augVal === v) || avg(aug.values) === v,
    )!;
    return ITEM_BY_REF("ITEM", augment.refName)![0];
  });
}

export function replaceHashWithValues(template: string, values: number[]) {
  let result = template;
  values.forEach((value: number) => {
    result = result.replace("#", value.toString()); // Replace the first occurrence of #
  });
  return result;
}

function isItemMissingItemClass(section: string[]): boolean {
  if (section.length > 3 || section.length < 2) return false;
  return section[0].startsWith(_$.RARITY) && section[1] !== undefined;
}

// Disable since this is export for tests
// eslint-disable-next-line @typescript-eslint/naming-convention
export const __testExports = {
  itemTextToSections,
  findInDatabase,
  parseNamePlate,
  isItemMissingItemClass,
  parseWeapon,
  parseArmour,
  parseModifiers,
  parseWaystone,
  parseRequirements,
  parseFractured,
  parseUnidentified,
  parseTrials,
  determineAugments,
  modifiedBfs,
  parseAugmentSockets,
  applyAugmentSockets,
};
